/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  Play,
} from "lucide-react";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";

const statusMap = {
  pending: "Pending",
  new: "New",
  inprogress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
  approved: "Approved",
  rejected: "Rejected",
};

const rawStatus = {
  Pending: "pending",
  New: "new",
  "In Progress": "inprogress",
  Completed: "completed",
  Cancelled: "cancelled",
  Approved: "approved",
  Rejected: "rejected",
};

const TrainingRequests = () => {
  const { trainings, fetchAllTrainings, updateTrainingStatus } =
    useContext(AdminContext);
  const [normalizedTrainings, setNormalizedTrainings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate input state
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  useEffect(() => {
    fetchAllTrainings();
  }, []);

  useEffect(() => {
    setNormalizedTrainings(
      trainings.map((t) => ({
        ...t,
        id: t.id || "unknown",
        type: t.trainingType,
        date: t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "N/A",
        participants: 1,
        user: {
          name: t.user?.name || t.name || "Unknown",
          email: t.user?.phone || t.user?.email || t.phone || t.email || "N/A",
          avatar:
            t.user?.image ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              t.user?.name || t.name || "Unknown",
            )}&background=random`,
        },
        status: (t.status || "pending").toLowerCase(),
      })),
    );
  }, [trainings]);

  const filteredTrainings = normalizedTrainings.filter((t) => {
    const matchesSearch =
      (t.user.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.user.email || "").includes(searchTerm) ||
      (t.id || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All" || t.status === rawStatus[statusFilter];
    return matchesSearch && matchesStatus;
  });

  const paginatedTrainings = filteredTrainings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const totalPages = Math.ceil(filteredTrainings.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  // Search handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStatusUpdate = (newDisplayStatus) => {
    if (selectedTraining) {
      const newRawStatus = rawStatus[newDisplayStatus];
      updateTrainingStatus(selectedTraining.id, newRawStatus);
      setShowUpdateModal(false);
      setSelectedTraining(null);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSearchInput("");
    setStatusFilter("All");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-orange-600 text-white dark:bg-orange-700 dark:text-white border-orange-700 dark:border-orange-800";
      case "new":
        return "bg-blue-600 text-white dark:bg-blue-700 dark:text-white border-blue-700 dark:border-blue-800";
      case "inprogress":
        return "bg-yellow-600 text-white dark:bg-yellow-700 dark:text-white border-yellow-700 dark:border-yellow-800";
      case "completed":
        return "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white border-emerald-700 dark:border-emerald-800";
      case "cancelled":
        return "bg-red-600 text-white dark:bg-red-700 dark:text-white border-red-700 dark:border-red-800";
      case "approved":
        return "bg-green-600 text-white dark:bg-green-700 dark:text-white border-green-700 dark:border-green-800";
      case "rejected":
        return "bg-gray-600 text-white dark:bg-gray-700 dark:text-white border-gray-700 dark:border-gray-800";
      default:
        return "bg-gray-600 text-white";
    }
  };

  const handleCloseModal = (e) => {
    if (e.target === e.currentTarget) {
      setShowUpdateModal(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Training Requests
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            Manage training sessions and status workflows.
          </p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border hover:bg-accent dark:hover:bg-gray-700 transition-colors w-full sm:w-auto"
        >
          <Filter size={20} className="text-gray-400" />
          {showFilters ? "Hide Filters" : "Show Filters"}
        </button>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <div className="bg-card dark:bg-dark-surface p-3 sm:p-4 rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border mb-6">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by user or Training ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium transition-all"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 lg:gap-0">
              {/* Search Button */}
              <button
                onClick={handleSearch}
                className="px-4 py-3 rounded-2xl bg-primary hover:bg-primary/90 text-white font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Search size={18} />
                <span className="sm:hidden">Search</span>
              </button>

              {/* Status Dropdown */}
              <div className="relative lg:min-w-[200px] lg:ml-3 dropdown-container">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="w-full flex items-center justify-between pl-4 pr-3 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <span>
                    {statusFilter === "All" ? "All Statuses" : statusFilter}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-primary transition-transform ${
                      showStatusDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showStatusDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                    {[
                      "All",
                      "Pending",
                      "New",
                      "In Progress",
                      "Completed",
                      "Cancelled",
                      "Approved",
                      "Rejected",
                    ].map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          setStatusFilter(status);
                          setShowStatusDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          statusFilter === status
                            ? "bg-primary text-white dark:bg-primary"
                            : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                        }`}
                      >
                        {status === "All" ? "All Statuses" : status}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={resetFilters}
                className="px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors lg:ml-3"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  ID
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  User
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Training Type
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Date
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Participants
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Status
                </th>
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {paginatedTrainings.map((t) => (
                <tr
                  key={t.id || Math.random()}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <td className="px-4 sm:px-6 py-4 font-mono text-gray-500 text-xs sm:text-sm">
                    <span className="hidden sm:inline">{t.id || "N/A"}</span>
                    <span className="sm:hidden">
                      {t.id ? t.id.substring(0, 8) + "..." : "N/A"}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-3">
                      {/* Show avatar only on medium screens and above */}
                      <img
                        src={
                          t.user?.avatar ||
                          `https://ui-avatars.com/api/?name=Unknown&background=random`
                        }
                        className="hidden md:block w-8 h-8 rounded-full object-cover"
                        alt={t.user?.name || "Unknown"}
                      />
                      <div>
                        <p className="font-bold text-text dark:text-dark-text">
                          {t.user?.name || "Unknown"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-text dark:text-dark-text">
                    {t.type || "N/A"}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-600 dark:text-dark-muted">
                    {t.date || "N/A"}
                  </td>
                  <td className="px-4 sm:px-6 py-4 font-bold text-text dark:text-dark-text pl-4 sm:pl-10">
                    {t.participants || 1}
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(
                        t.status,
                      )}`}
                    >
                      {statusMap[t.status] || t.status || "Unknown"}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-center">
                    <button
                      onClick={() => {
                        setSelectedTraining(t);
                        setShowUpdateModal(true);
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <MoreHorizontal size={18} />
                    </button>
                  </td>
                </tr>
              ))}
              {paginatedTrainings.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No training requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>

      {/* Workflow Modal */}
      {showUpdateModal && selectedTraining && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          <div
            className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-text dark:text-dark-text">
                Update Status
              </h3>
              <button onClick={() => setShowUpdateModal(false)}>
                <XCircle className="text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-dark-muted mb-1">
                  User
                </p>
                <p className="font-bold text-text dark:text-dark-text">
                  {selectedTraining.user?.name || "Unknown"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-dark-muted mb-1">
                  Training Type
                </p>
                <p className="font-bold text-text dark:text-dark-text">
                  {selectedTraining.type || "N/A"}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
                <p className="text-sm font-medium text-gray-500 dark:text-dark-muted mb-1">
                  Current Status
                </p>
                <span
                  className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(
                    selectedTraining.status,
                  )}`}
                >
                  {statusMap[selectedTraining.status] ||
                    selectedTraining.status ||
                    "Unknown"}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {/* Pending status - same as new, can approve, reject, start, or cancel */}
              {selectedTraining.status === "pending" && (
                <>
                  <button
                    onClick={() => handleStatusUpdate("Approved")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-green-500"
                  >
                    <CheckCircle size={18} /> Approve Training
                  </button>
                  <button
                    onClick={() => handleStatusUpdate("In Progress")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-yellow-500"
                  >
                    <Play size={18} /> Start Training
                  </button>
                  <button
                    onClick={() => handleStatusUpdate("Rejected")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-gray-500"
                  >
                    <XCircle size={18} /> Reject
                  </button>
                  <button
                    onClick={() => handleStatusUpdate("Cancelled")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-red-500"
                  >
                    <XCircle size={18} /> Cancel
                  </button>
                </>
              )}

              {/* New status - can approve, reject, start, or cancel */}
              {selectedTraining.status === "new" && (
                <>
                  <button
                    onClick={() => handleStatusUpdate("Approved")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-green-500"
                  >
                    <CheckCircle size={18} /> Approve Training
                  </button>
                  <button
                    onClick={() => handleStatusUpdate("In Progress")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-yellow-500"
                  >
                    <Play size={18} /> Start Training
                  </button>
                  <button
                    onClick={() => handleStatusUpdate("Rejected")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-gray-500"
                  >
                    <XCircle size={18} /> Reject
                  </button>
                  <button
                    onClick={() => handleStatusUpdate("Cancelled")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-red-500"
                  >
                    <XCircle size={18} /> Cancel
                  </button>
                </>
              )}

              {/* Approved status - can start or cancel */}
              {selectedTraining.status === "approved" && (
                <>
                  <button
                    onClick={() => handleStatusUpdate("In Progress")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-yellow-500"
                  >
                    <Play size={18} /> Start Training
                  </button>
                  <button
                    onClick={() => handleStatusUpdate("Cancelled")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-red-500"
                  >
                    <XCircle size={18} /> Cancel
                  </button>
                </>
              )}

              {/* In Progress status - can complete or cancel */}
              {selectedTraining.status === "inprogress" && (
                <>
                  <button
                    onClick={() => handleStatusUpdate("Completed")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-emerald-500"
                  >
                    <CheckCircle size={18} /> Mark Completed
                  </button>
                  <button
                    onClick={() => handleStatusUpdate("Cancelled")}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-red-500"
                  >
                    <XCircle size={18} /> Cancel
                  </button>
                </>
              )}

              {/* Final statuses - no actions available */}
              {["completed", "cancelled", "rejected"].includes(
                selectedTraining.status,
              ) && (
                <p className="text-center text-gray-500 dark:text-dark-muted text-sm">
                  No actions available for this status.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingRequests;
